import { Html, html, View } from 'rune-ts';
import klass from './PopularTopNProductSection.module.scss';
import { bestProductTypeMap, ProductCardViewData } from '@services/MarppleShop/features/ProductList/type';
import { CategoryLevel1_CODE } from '../../../../../../../shared/mps-shared/domain/category/types';
import { ProductListApiS } from '@services/MarppleShop/features/ProductList/outbound/share';
import {
  createBrandDealUrlBuilder,
  createEbookUrlBuilder,
  createGoodsUrlBuilder,
  createHandmadeUrlBuilder,
} from '@services/MarppleShop/features/ProductList/util';
import {
  createCategoryButtonGroupView,
  SingleSelectButtonGroup,
} from '@services/MarppleShop/renderApp/components/cells/SingleSelectButtonGroup';
import { font } from '@services/MarppleShop/shared/typography';
import { SeeMoreLinkPlugin } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/plugins';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle';
import { map, pipe, take, toArray } from '@fxts/core';
import { CardProductControllerView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductControllerView.ts';
import { Title } from '@services/MarppleShop/renderApp/components/atoms/Title/Title.ts';

export type PopularTopNProductSectionData = {
  section_title: {
    title: string | Html;
    sub_title: string;
    see_more_link: string;
  };
  products: ProductCardViewData[];
};

type PopularTopNProductSectionOption = {
  is_mobile: boolean;
};

const i18n = {
  get goods() {
    return ET('mps2::gnb::menu::goods');
  },
  get handmade() {
    return ET('mps2::gnb::menu::handmade');
  },
  get brandDeal() {
    return ET('mps2::gnb::menu::brand_deal');
  },
  get ebook() {
    return ET('mps2::gnb::menu::ebook');
  },
};

const CATE_ROOT_ID_MAP = {
  [CategoryLevel1_CODE.GOODS]: 1,
  [CategoryLevel1_CODE.HANDMADE]: 2,
  [CategoryLevel1_CODE.BRAND_DEAL]: 3,
  [CategoryLevel1_CODE.EBOOK]: 4,
} as const;

export class PopularTopNProductSectionView extends View<PopularTopNProductSectionData> {
  private current_root_cate_code: CategoryLevel1_CODE = CategoryLevel1_CODE.GOODS;
  private product_card_lazy = true;
  private singleSelectButtonGroup: SingleSelectButtonGroup<CategoryLevel1_CODE>;

  private static categoryLevel1Items = [
    {
      id: CategoryLevel1_CODE.GOODS,
      display_name: i18n.goods,
      value: CategoryLevel1_CODE.GOODS,
    },
    {
      id: CategoryLevel1_CODE.HANDMADE,
      display_name: i18n.handmade,
      value: CategoryLevel1_CODE.HANDMADE,
    },
    {
      id: CategoryLevel1_CODE.BRAND_DEAL,
      display_name: i18n.brandDeal,
      value: CategoryLevel1_CODE.BRAND_DEAL,
    },
    {
      id: CategoryLevel1_CODE.EBOOK,
      display_name: i18n.ebook,
      value: CategoryLevel1_CODE.EBOOK,
    },
  ];

  constructor(
    data: PopularTopNProductSectionData,
    private option: PopularTopNProductSectionOption,
  ) {
    super(data, option);

    this.singleSelectButtonGroup = createCategoryButtonGroupView<CategoryLevel1_CODE>(
      {
        items: PopularTopNProductSectionView.categoryLevel1Items.filter((item) =>
          ET.lang !== 'kr' ? item.id === CategoryLevel1_CODE.GOODS : true,
        ),
        selected_id: this.current_root_cate_code,
      },
      {
        onChange: (item) => {
          item.value && this.selectRootCategory(item.value);
        },
      },
      {
        size: this.option.is_mobile ? 'xs' : 'xl',
      },
    );
  }

  override template() {
    const { products } = this.data;
    const { is_mobile } = this.option;
    const title = html`
      <div class="${is_mobile ? klass.title_wrap_mo : klass.title_wrap_pc}">
        ${this.data.section_title.title}${this.singleSelectButtonGroup}
      </div>
    `;
    return html`
      <section class="${klass.container}">
        <!-- Section Title -->
        ${is_mobile
          ? new Title({
              ...this.data.section_title,
              size: 'large',
              title,
              custom_klass: klass.with_100,
            })
          : new SectionTitle({
              title_props: {
                ...this.data.section_title,
                size: 'xLarge',
                title,
              },
              plugins: [
                new SeeMoreLinkPlugin({
                  see_more_link: this.data.section_title.see_more_link,
                  size: is_mobile ? 'xSmall' : 'medium',
                  theme: 'light',
                }),
              ],
            })}

        <div class="${klass.product_list}">
          ${pipe(
            products,
            map(
              (product) =>
                new CardProductControllerView({
                  card_product_view_props: is_mobile
                    ? {
                        type: 'E',
                        size: 'Xsmall',
                        theme: 'Dark',
                        layout: 'Vertical',
                      }
                    : {
                        type: 'E',
                        size: 'Large',
                        theme: 'Light',
                        layout: 'Vertical',
                      },
                  product_card_view_data: product,
                  options: {
                    resize_width: 600,
                  },
                }),
            ),
            take(is_mobile ? 9 : 10),
            toArray,
          )}
        </div>
        ${is_mobile
          ? html`<a
              href="${this.data.section_title.see_more_link}"
              class="${klass.see_more_mo} ${font('14_medium')}"
            >
              ${html.preventEscape(this.getMobileSeeMoreStr())}
            </a>`
          : ''}
      </section>
    `;
  }

  private getMobileSeeMoreStr() {
    const item = PopularTopNProductSectionView.categoryLevel1Items.find(({ id }) => {
      return id == this.current_root_cate_code;
    });
    if (!item || this.current_root_cate_code == CategoryLevel1_CODE.GOODS) {
      return ET('mps2::main::top_100::see_more');
    }
    return `<span>${item.display_name} TOP 100</span>&nbsp;더보기`;
  }

  private getUrlByCode(code: CategoryLevel1_CODE): string {
    /**
     *  handmade, brand-deal, ebook 상품은 너무 없을 경우를 대비해서 우선 monthly
     *  history: https://marpple.slack.com/archives/C05Q5T9TH8S/p1753164512932599?thread_ts=1753164483.971709&cid=C05Q5T9TH8S
     */
    switch (code) {
      case CategoryLevel1_CODE.GOODS: {
        return createGoodsUrlBuilder().build();
      }
      case CategoryLevel1_CODE.HANDMADE: {
        return createHandmadeUrlBuilder().setRankType('MONTHLY').build();
      }
      case CategoryLevel1_CODE.BRAND_DEAL: {
        return createBrandDealUrlBuilder().setRankType('MONTHLY').build();
      }
      case CategoryLevel1_CODE.EBOOK: {
        return createEbookUrlBuilder().setRankType('MONTHLY').build();
      }
      default: {
        return createGoodsUrlBuilder().build();
      }
    }
  }

  private async selectRootCategory(code: CategoryLevel1_CODE) {
    this.current_root_cate_code = code;
    this.product_card_lazy = false;

    this.data.section_title.see_more_link = this.getUrlByCode(this.current_root_cate_code);

    const { products } = await ProductListApiS.getBestProductListApi({
      limit: 10,
      exclude_sold_out: false,
      /**
       *  handmade, brand-deal, ebook 상품은 너무 없을 경우를 대비해서 우선 monthly
       *  history: https://marpple.slack.com/archives/C05Q5T9TH8S/p1753164512932599?thread_ts=1753164483.971709&cid=C05Q5T9TH8S
       */
      rank_type:
        this.current_root_cate_code === CategoryLevel1_CODE.GOODS
          ? bestProductTypeMap.realtime
          : bestProductTypeMap.monthly,
      cate_root_id: CATE_ROOT_ID_MAP[code],
    });

    this.data.products = products;

    this.redraw();
  }
}
